import { FC } from 'react';
import { Box, css, styled, Theme, Typography } from '@mui/material';
import classNames from 'classnames';

import { Button, ButtonProps, Menu } from '@components/base';

type ToolbarButtonProps = Omit<ButtonProps, 'children' | 'component'> & {
  isLastItem?: boolean;
  isMenuButton?: boolean;
  subtitle?: string;
  title: string;
};

const getButtonCSS = (theme: Theme) => css`
  border-radius: 0;
  height: 100%;
  padding-left: ${theme.spacing(1.5)};
  padding-right: ${theme.spacing(1.5)};
  width: 100%;
  min-height: 38px;

  ${theme.breakpoints.up('md')} {
    min-height: 44px;
  }

  ${theme.breakpoints.up('lg')} {
    min-height: 56px;
  }

  &.last {
    ${theme.breakpoints.up('md')} {
      padding-right: ${theme.spacing(2)};
    }
  }

  &:focus-visible {
    background: ${theme.palette.action.focus};
  }

  .buttonText {
    margin-right: auto;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }
`;

const StyledButton = styled(Button)`
  ${({ theme }) => getButtonCSS(theme)}
`;

const StyledMenuButton = styled(Menu.Button)`
  ${({ theme }) => getButtonCSS(theme)}
` as typeof Menu.Button;

const ToolbarButton: FC<ToolbarButtonProps> = ({
  className,
  isLastItem,
  isMenuButton,
  subtitle,
  title,
  ...rest
}) => {
  const Component = isMenuButton ? StyledMenuButton : StyledButton;

  return (
    <Component
      className={classNames(className, { last: isLastItem })}
      color='inherit'
      disableFocusRipple
      fullWidth
      {...rest}
    >
      {subtitle ? (
        <Box className='buttonText' component='span'>
          <Typography
            component='span'
            lineHeight={1.5}
            noWrap
            textTransform='none'
            variant='caption'
          >
            {title}
          </Typography>

          <Typography
            component='span'
            lineHeight={1.5}
            noWrap
            textTransform={'none'}
            variant='inherit'
          >
            {subtitle}
          </Typography>
        </Box>
      ) : (
        <Typography className='buttonText' variant='inherit'>
          {title}
        </Typography>
      )}
    </Component>
  );
};

export default ToolbarButton;
